import { GraduationCap, Star, Film, Handshake, Package, Activity, ArrowRight } from "lucide-react";

/**
 * Item #1 — "How CastReady connects" one-screen ecosystem diagram.
 *
 * Converts the four product cards from "alternatives" into a "stack".
 * Coach assigns → actor practices in Callback Zone → Castings receives
 * the tape → Pro powers the partner exposure + co-branded kit loop.
 * Training Kits and Readiness sit in the middle as the shared
 * connective tissue (matches the moat language in the competitive
 * landscape report).
 */
const STEPS = [
  { icon: GraduationCap, label: "Coach", sub: "Assign work" },
  { icon: Star,          label: "Callback Zone", sub: "Practice + tapes" },
  { icon: Film,          label: "Castings", sub: "Submit + book" },
  { icon: Handshake,     label: "Pro", sub: "Partner exposure" },
] as const;

export default function EcosystemConnectionDiagram() {
  return (
    <div className="mb-8 rounded-2xl border border-border bg-card/60 backdrop-blur p-5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-primary mb-3 text-center">
        How CastReady connects
      </p>

      {/* Step rail */}
      <div className="flex flex-col md:flex-row md:items-stretch md:gap-2">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="flex md:flex-1 items-center md:items-stretch gap-2 md:gap-0">
              <div className="flex-1 rounded-xl border border-border/80 bg-background px-3 py-2.5 md:py-3 flex md:flex-col items-center md:items-center gap-2 md:gap-1.5 text-center">
                <span className="inline-flex h-7 w-7 md:h-8 md:w-8 items-center justify-center rounded-lg bg-primary/10 text-primary shrink-0">
                  <Icon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                </span>
                <div className="min-w-0">
                  <p className="text-xs md:text-sm font-semibold leading-tight">{s.label}</p>
                  <p className="text-[10px] md:text-xs text-muted-foreground leading-tight">{s.sub}</p>
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <ArrowRight className="hidden md:block w-4 h-4 mx-1 self-center text-muted-foreground shrink-0" />
              )}
            </div>
          );
        })}
      </div>

      {/* Connective tissue row */}
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className="rounded-lg border border-dashed border-primary/30 bg-primary/[0.04] px-3 py-2 flex items-center gap-2">
          <Package className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="text-[11px] md:text-xs text-muted-foreground leading-snug">
            <span className="font-medium text-foreground">Training Kits</span> feed practice — physical + digital.
          </span>
        </div>
        <div className="rounded-lg border border-dashed border-primary/30 bg-primary/[0.04] px-3 py-2 flex items-center gap-2">
          <Activity className="h-3.5 w-3.5 text-primary shrink-0" />
          <span className="text-[11px] md:text-xs text-muted-foreground leading-snug">
            <span className="font-medium text-foreground">Readiness Data</span> connects every step — coach to actor to casting.
          </span>
        </div>
      </div>
    </div>
  );
}